
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { CSOLoadingSpinner } from "@/components/ui/cso-loading-spinner";

const EnterpriseOnboarding = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [formData, setFormData] = useState({
    organizationName: "",
    subdomain: "",
    companySize: "unlimited",
    primaryColor: "#3b82f6",
    secondaryColor: "#64748b",
    logoUrl: "",
    contactEmail: "",
    industry: ""
  });

  useEffect(() => {
    const verifyEnterpriseSubscription = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate('/auth');
          return;
        }

        // Check if user has enterprise subscription
        const { data: subscription } = await supabase
          .from('user_subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .eq('subscription_type', 'enterprise')
          .eq('status', 'active')
          .single();

        if (!subscription) {
          toast({
            title: "Access Denied",
            description: "You need an active enterprise subscription to access this page.",
            variant: "destructive",
          });
          navigate('/plans');
          return;
        }

        // Check if organization already exists
        const { data: existingOrg } = await supabase
          .from('organization_memberships')
          .select('organization_id, organizations(*)')
          .eq('user_id', user.id)
          .eq('role', 'owner')
          .single();

        if (existingOrg) {
          navigate(`/organization/${existingOrg.organization_id}`);
          return;
        }

      } catch (error) {
        console.error('Error verifying enterprise subscription:', error);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyEnterpriseSubscription();
  }, [navigate, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create organization with branding
      const { data: organization, error: orgError } = await supabase
        .from('organizations')
        .insert({
          name: formData.organizationName,
          slug: formData.organizationName.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          subdomain: formData.subdomain,
          tier: 'enterprise',
          max_seats: -1, // Unlimited for enterprise
          branding: {
            companyName: formData.organizationName,
            primaryColor: formData.primaryColor,
            secondaryColor: formData.secondaryColor,
            logo: formData.logoUrl,
            contactEmail: formData.contactEmail,
            industry: formData.industry
          }
        })
        .select()
        .single();

      if (orgError) throw orgError;

      // Create owner membership
      const { error: membershipError } = await supabase
        .from('organization_memberships')
        .insert({
          organization_id: organization.id,
          user_id: user.id,
          role: 'owner'
        });

      if (membershipError) throw membershipError;

      toast({
        title: "Enterprise Organization Created!",
        description: "Your enterprise organization has been set up with custom branding. Welcome to CSO Agent Enterprise!",
      });

      navigate(`/organization/${organization.id}`);

    } catch (error: any) {
      console.error('Error creating enterprise organization:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create enterprise organization",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CSOLoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-extrabold text-gray-900">
            Enterprise Setup
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Configure your enterprise organization with custom branding and settings
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Enterprise Configuration</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="organization" className="space-y-6">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="organization">Organization</TabsTrigger>
                <TabsTrigger value="branding">Branding</TabsTrigger>
              </TabsList>

              <TabsContent value="organization" className="space-y-6">
                <div>
                  <Label htmlFor="organizationName">Organization Name</Label>
                  <Input
                    id="organizationName"
                    type="text"
                    required
                    value={formData.organizationName}
                    onChange={(e) => setFormData(prev => ({ ...prev, organizationName: e.target.value }))}
                    placeholder="Enterprise Corporation"
                  />
                </div>

                <div>
                  <Label htmlFor="subdomain">Custom Subdomain</Label>
                  <Input
                    id="subdomain"
                    type="text"
                    value={formData.subdomain}
                    onChange={(e) => setFormData(prev => ({ ...prev, subdomain: e.target.value }))}
                    placeholder="enterprise"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Your organization will be accessible at: {formData.subdomain || 'your-subdomain'}.csoagent.com
                  </p>
                </div>

                <div>
                  <Label htmlFor="contactEmail">Contact Email</Label>
                  <Input
                    id="contactEmail"
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData(prev => ({ ...prev, contactEmail: e.target.value }))}
                    placeholder="contact@enterprise.com"
                  />
                </div>

                <div>
                  <Label htmlFor="industry">Industry</Label>
                  <Input
                    id="industry"
                    type="text"
                    value={formData.industry}
                    onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                    placeholder="Technology, Healthcare, Finance, etc."
                  />
                </div>
              </TabsContent>

              <TabsContent value="branding" className="space-y-6">
                <div>
                  <Label htmlFor="logoUrl">Logo URL</Label>
                  <Input
                    id="logoUrl"
                    type="url"
                    value={formData.logoUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, logoUrl: e.target.value }))}
                    placeholder="https://example.com/logo.png"
                  />
                  {formData.logoUrl && (
                    <div className="mt-2">
                      <img
                        src={formData.logoUrl}
                        alt="Logo preview"
                        className="h-12 w-auto"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="primaryColor">Primary Brand Color</Label>
                  <div className="flex items-center space-x-2">
                    <input
                      id="primaryColor"
                      type="color"
                      value={formData.primaryColor}
                      onChange={(e) => setFormData(prev => ({ ...prev, primaryColor: e.target.value }))}
                      className="w-20 h-10 rounded border"
                    />
                    <Input
                      value={formData.primaryColor}
                      onChange={(e) => setFormData(prev => ({ ...prev, primaryColor: e.target.value }))}
                      placeholder="#3b82f6"
                      className="flex-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="secondaryColor">Secondary Brand Color</Label>
                  <div className="flex items-center space-x-2">
                    <input
                      id="secondaryColor"
                      type="color"
                      value={formData.secondaryColor}
                      onChange={(e) => setFormData(prev => ({ ...prev, secondaryColor: e.target.value }))}
                      className="w-20 h-10 rounded border"
                    />
                    <Input
                      value={formData.secondaryColor}
                      onChange={(e) => setFormData(prev => ({ ...prev, secondaryColor: e.target.value }))}
                      placeholder="#64748b"
                      className="flex-1"
                    />
                  </div>
                </div>

                <div className="pt-4">
                  <h4 className="font-medium mb-2">Brand Preview</h4>
                  <div 
                    className="p-4 rounded-lg border"
                    style={{ 
                      backgroundColor: formData.primaryColor + '10',
                      borderColor: formData.primaryColor
                    }}
                  >
                    <div className="flex items-center space-x-3">
                      {formData.logoUrl && (
                        <img src={formData.logoUrl} alt="Logo" className="h-8 w-auto" />
                      )}
                      <span 
                        className="font-semibold"
                        style={{ color: formData.primaryColor }}
                      >
                        {formData.organizationName || "Your Enterprise"}
                      </span>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <div className="pt-6">
              <Button
                onClick={handleSubmit}
                disabled={isLoading}
                className="w-full bg-cso-600 hover:bg-cso-700"
              >
                {isLoading ? "Setting Up Enterprise..." : "Complete Enterprise Setup"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EnterpriseOnboarding;
